import type { ReconciliationSession, ReconciliationSessionSummary } from '../types'
import { apiFetch, getAuthHeader, handleUnauthorized, downloadBlob } from './core'

export interface SendEmailResult {
  sent: number
  to: string
  or_ref: string | null
  da_ref: string | null
  inv_ref: string | null
}

export type PdfMode = 'none' | 'file' | 'inline' | 'both'

export interface PdfAttachOptions {
  ordrsp_file: boolean
  ordrsp_inline: boolean
  desadv_file: boolean
  desadv_inline: boolean
  invoice_file: boolean
  invoice_inline: boolean
  report: boolean
}

export interface SendEmailOptions {
  strip_pdf: boolean
  pdf_ordrsp: PdfMode
  pdf_desadv: PdfMode
  pdf_invoice: PdfMode
  pdf_report: boolean
  include_ordrsp: boolean
  include_desadv: boolean
  include_invoice: boolean
  test_case_label?: string
  scenario_name?: string
  to_address?: string
}

export function toPdfMode(file: boolean, inline: boolean): PdfMode {
  if (file && inline) return 'both'
  if (file) return 'file'
  if (inline) return 'inline'
  return 'none'
}

export interface SendEmailStreamEvent {
  type: 'start' | 'sending' | 'sent' | 'done' | 'error'
  total?: number
  index?: number
  doc_type?: string
  label?: string
  sent?: number
  to?: string
  or_ref?: string | null
  da_ref?: string | null
  inv_ref?: string | null
  detail?: string
}

export async function uploadFiles(files: File[]): Promise<ReconciliationSession> {
  const form = new FormData()
  files.forEach((f) => form.append('files', f))
  const res = await fetch('/api/reconcile', {
    method: 'POST',
    headers: { Authorization: getAuthHeader() },
    body: form,
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  return res.json()
}

export async function getHistory(): Promise<ReconciliationSessionSummary[]> {
  return apiFetch<ReconciliationSessionSummary[]>('/api/history')
}

export async function getSession(id: number): Promise<ReconciliationSession> {
  return apiFetch<ReconciliationSession>(`/api/history/${id}`)
}

export async function deleteSession(id: number): Promise<void> {
  return apiFetch<void>(`/api/history/${id}`, { method: 'DELETE' })
}

export async function sendEmailStream(
  sessionId: number,
  options: SendEmailOptions,
  onEvent: (event: SendEmailStreamEvent) => void,
): Promise<void> {
  const res = await fetch(`/api/reconcile/${sessionId}/send-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: getAuthHeader() },
    body: JSON.stringify(options),
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  const reader = res.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          onEvent(JSON.parse(line.slice(6)) as SendEmailStreamEvent)
        } catch { /* ongeldige chunk — negeer */ }
      }
    }
  }
}

export async function downloadSessionZip(sessionId: number): Promise<void> {
  const res = await fetch(`/api/reconcile/${sessionId}/download`, {
    headers: { Authorization: getAuthHeader() },
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  await downloadBlob(res, `download-${sessionId}.zip`)
}

export async function fetchSessionXml(sessionId: number, docType: string): Promise<string> {
  const res = await fetch(`/api/reconcile/${sessionId}/xml/${docType}`, {
    headers: { Authorization: getAuthHeader() },
  })
  handleUnauthorized(res)
  if (!res.ok) throw new Error('XML niet beschikbaar')
  return res.text()
}

export interface DiffLine {
  left: string | null
  right: string | null
  status: 'equal' | 'changed' | 'added' | 'removed'
}

export interface LineDiffResult {
  doc_a: string
  doc_b: string
  lines: DiffLine[]
}

export async function fetchLineDiff(
  sessionId: number,
  lineId: number,
  docA: string,
  docB: string,
): Promise<LineDiffResult> {
  return apiFetch<LineDiffResult>(
    `/api/reconcile/${sessionId}/diff/${lineId}?doc_a=${docA}&doc_b=${docB}`,
  )
}

export interface FindingsEmailPayload {
  to_address: string
  subject: string
  body: string
  attach_ordrsp: boolean
  attach_desadv: boolean
  attach_invoice: boolean
  use_parties_naming: boolean
}

export interface FindingsEmailResult {
  sent: boolean
  to: string
  attachments: number
}

export async function sendFindingsEmail(
  sessionId: number,
  payload: FindingsEmailPayload,
): Promise<FindingsEmailResult> {
  const res = await fetch(`/api/reconcile/${sessionId}/send-findings-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: getAuthHeader() },
    body: JSON.stringify(payload),
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  return res.json()
}

export async function fetchPdfBlobUrl(sessionId: number, docType: string): Promise<string> {
  const res = await fetch(`/api/reconcile/${sessionId}/pdf/${docType}`, {
    headers: { Authorization: getAuthHeader() },
  })
  handleUnauthorized(res)
  if (!res.ok) throw new Error('PDF niet beschikbaar')
  const blob = await res.blob()
  return URL.createObjectURL(blob)
}
